import type { ButtonHTMLAttributes } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
}

const VARIANT_CLASSES: Record<Variant, string> = {
  primary: 'bg-primary text-primary-foreground hover:opacity-90 focus-visible:ring-primary/40',
  secondary:
    'border border-border bg-background text-foreground hover:bg-muted focus-visible:ring-primary/40',
  ghost: 'text-foreground hover:bg-muted focus-visible:ring-primary/40',
};

// Shared with non-<button> elements that need to look like a Button but
// can't be one — e.g. a file-download <a>, which needs an `href` a native
// <button> can't carry.
export function buttonClassName(variant: Variant = 'primary', className = ''): string {
  return [
    // ~44px tall — Apple HIG touch target. Smaller buttons are tedious
    // for the on-phone-under-stress manager DESIGN §1.1 calls out.
    // §6 — buttons use the 8px (rounded-sm) radius. Min-height ~48px.
    'inline-flex items-center justify-center gap-2 rounded-sm px-4 py-2.5 text-base font-medium',
    'transition focus-visible:ring-2 focus-visible:outline-none',
    'disabled:cursor-not-allowed disabled:opacity-60',
    VARIANT_CLASSES[variant],
    className,
  ].join(' ');
}

export function Button({
  variant = 'primary',
  loading,
  className,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled ?? loading}
      className={buttonClassName(variant, className ?? '')}
    >
      {loading ? '…' : children}
    </button>
  );
}
